import { Grid, Skeleton, Switch } from '@mui/material';
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { validateEmail } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalDialog, RoutalPalette, RoutalSelect, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import CategoricalIcon from '../../../../assets/img/custom_field-categorical.svg';
import PictureIcon from '../../../../assets/img/custom_field-picture.svg';
import SignatureIcon from '../../../../assets/img/custom_field-signature.svg';
import TextIcon from '../../../../assets/img/custom_field-text.svg';
import CommunicationPictureIcon from '../../../../assets/svg/CommunicationPictureIcon';
import CommunicationSignatureIcon from '../../../../assets/svg/CommunicationSignatureIcon';
import MessageIcon from '../../../../assets/svg/MessageIcon';
import RatingStar from '../../../../assets/svg/RatingStar';
import StopReportIcon from '../../../../assets/svg/right-panel-tab-icons/StopReportIcon';
import { Project } from '../../../../domain/project/Project';
import { ProjectCustomerEmailTypes } from '../../../../domain/project/ProjectDTO';
import { ProjectCustomerEmailItem } from '../../../../domain/project/projectCustomerEmail/ProjectCustomerEmailItem';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useSendTestCommunicationEmail } from '../../../../hooks/project/useSendTestCommunicationEmail';
import { useUpdateProjectCommunicationEmails } from '../../../../hooks/project/useUpdateProjectCommunicationEmails';
import Button from '../../../../react-components/Button';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import { ProjectCustomFieldTypeIcon } from '../../../project-settings/custom_fields/ProjectCustomFieldIcons';
import EditableContentEmail from './EditableContentEmail';
import {
  AttachmentItem,
  NotificationsEmailBody,
  NotificationsEmailBodyFooter,
  NotificationsEmailContainer,
  NotificationsEmailContainerBody,
  NotificationsEmailContainerHeader,
} from './NotificationsEmails.styled';

export const podPopoverConfig = {
  anchorOrigin: {
    vertical: `bottom`,
    horizontal: `center`,
  },
  transformOrigin: {
    vertical: `top`,
    horizontal: `center`,
  },
};

const CUSTOM_FIELD_PREFIX = `custom_fields.`;

const customerEmailSubstitutions = [
  `label`,
  `external_id`,
  `location_details`,
  `comments`,
  `email`,
  `reference_person`,
  `phone`,
  `url`,
  `pin`,
];

const htmlToString = (text?: string | null) => {
  return text ? text.replace(/<br\/>/g, `\n`) : undefined;
};

const stringToHtml = (text?: string | null) => {
  return text ? text.replace(/\n/g, `<br/>`) : undefined;
};

type IPodField = {
  id: string;
  label: string;
  icon: React.ReactElement;
};

type NotificationsEmailsProps = {
  emailType: ProjectCustomerEmailTypes;
  userEmail?: string;
  project: Project;
};

export const NotificationsEmailTemplate = ({ emailType, userEmail, project }: NotificationsEmailsProps) => {
  const [isPodDialogOpen, setIsPodDialogOpen] = useState<boolean>(false);
  const [isSendTestDialogOpen, setIsSendTestDialogOpen] = useState<boolean>(false);
  const [isEnableEmailDialogOpen, setIsEnableEmailDialogOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<ProjectCustomerEmailItem | undefined>(undefined);
  const [emailTemplate, setEmailTemplate] = useState<ProjectCustomerEmailItem | undefined>();
  const [availablePod, setAvailablePod] = useState<IPodField[] | undefined>();
  const [testEmail, setTestEmail] = useState<string>(userEmail ?? ``);
  const { run: editCommunicationEmails } = useUpdateProjectCommunicationEmails({ project });
  const { run: sendTestCommunication } = useSendTestCommunicationEmail({ project });
  const [visibleSubstitutions, setVisibleSubstitutions] = useState<boolean>(false);

  useEffect(() => {
    // Only showing the pickup emails if pickups are available in project view constraints.
    if (!project.customer_email) {
      return;
    }

    const emailData = project.customer_email[emailType];

    if (!emailData) {
      return;
    }
    const body = htmlToString(emailData.body);
    setEmailTemplate(
      ProjectCustomerEmailItem.create({
        from: emailData.from,
        enabled: emailData.enabled,
        subject: emailData.subject,
        updated_at: emailData.updated_at,
        pod_fields: emailData.pod_fields,
        body,
      })
    );

    let completedPodFields: IPodField[];

    switch (emailType) {
      case `stop_completed`:
      case `pickup_completed`:
        completedPodFields = [
          ...(project.drivers.stopCompletedFieldsHasSpecificField(`images`)
            ? [
                {
                  id: `pictures`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_completed.pictures`,
                  }),
                  icon: <img alt="" draggable="false" src={PictureIcon} style={{ width: `12px`, height: `12px` }} />,
                },
              ]
            : []),
          ...(project.drivers.stopCompletedFieldsHasSpecificField(`comments`)
            ? [
                {
                  id: `comments`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_completed.comments`,
                  }),
                  icon: <img alt="" draggable="false" src={TextIcon} style={{ width: `12px`, height: `12px` }} />,
                },
              ]
            : []),
          ...(project.drivers.stopCompletedFieldsHasSpecificField(`signature`)
            ? [
                {
                  id: `signature`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_completed.signature`,
                  }),
                  icon: <img alt="" draggable="false" src={SignatureIcon} style={{ width: `12px`, height: `12px` }} />,
                },
              ]
            : []),
        ];
        setAvailablePod([
          ...(completedPodFields as IPodField[]),
          ...((project.custom_fields?.service_report_completed ?? [])
            .filter((cf) => project.drivers.stopCompletedHasCustomField(cf.custom_id))
            .map((field: CustomFieldDefinition) => ({
              id: field.custom_id,
              label: field.label,
              icon: <ProjectCustomFieldTypeIcon fieldType={field.field_type} />,
            })) as IPodField[]),
        ]);
        break;
      case `stop_canceled`:
      case `pickup_canceled`:
        completedPodFields = [
          ...(project.drivers.stopCanceledFieldsHasSpecificField(`images`)
            ? [
                {
                  id: `pictures`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_canceled.pictures`,
                  }),
                  icon: <img alt="" draggable="false" src={PictureIcon} style={{ width: `12px`, height: `12px` }} />,
                },
              ]
            : []),
          ...(project.drivers.stopCanceledFieldsHasSpecificField(`comments`)
            ? [
                {
                  id: `comments`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_canceled.comments`,
                  }),
                  icon: <img alt="" draggable="false" src={TextIcon} style={{ width: `12px`, height: `12px` }} />,
                },
              ]
            : []),
          ...(project.drivers.stopCanceledFieldsHasSpecificField(`cancel_reason`)
            ? [
                {
                  id: `canceled_categorical`,
                  label: intl.formatMessage({
                    id: `projects.settings.fields.service_report_canceled.canceled_categorical`,
                  }),
                  icon: (
                    <img alt="" draggable="false" src={CategoricalIcon} style={{ width: `12px`, height: `12px` }} />
                  ),
                },
              ]
            : []),
        ];
        setAvailablePod([
          ...(completedPodFields as IPodField[]),
          ...((project.custom_fields?.service_report_canceled ?? [])
            .filter((cf) => project.drivers.stopCanceledHasCustomField(cf.custom_id))
            .map((field: CustomFieldDefinition) => ({
              id: field.custom_id,
              label: field.label,
              icon: <ProjectCustomFieldTypeIcon fieldType={field.field_type} />,
            })) as IPodField[]),
        ]);
        break;
      default:
        setAvailablePod(undefined);
        break;
    }
  }, [emailType, project]);

  const getCustomersLink = () => {
    let status;

    switch (emailType) {
      case `stop_created`:
      case `pickup_created`:
        status = `created`;
        break;
      case `route_started`:
      case `stop_approaching`:
      case `pickup_approaching`:
        status = `on_route`;
        break;
      case `stop_canceled`:
      case `pickup_canceled`:
        status = `canceled`;
        break;
      case `stop_completed`:
      case `pickup_completed`:
        status = `completed`;
        break;
      default:
        return ``;
    }
    return `${project.getCustomersDomain()}/example?status=${status}`;
  };

  const updateEmailItem = async (itemToUpdate?: ProjectCustomerEmailItem) => {
    if (!itemToUpdate) return;

    const body = stringToHtml(itemToUpdate.body);
    editCommunicationEmails({
      emailType,
      emailData: {
        ...(body ? { body } : {}),
        from: itemToUpdate.from,
        subject: itemToUpdate.subject,
        enabled: itemToUpdate.enabled,
        pod_fields: itemToUpdate.pod_fields ?? [],
      },
    });
  };

  const sendTestItem = async () => {
    if (selectedItem) {
      const body = stringToHtml(selectedItem.body);
      sendTestCommunication({
        type: emailType,
        email: testEmail,
        from: selectedItem.from,
        subject: selectedItem.subject,
        ...(body ? { body } : {}),
      });
    }
    setSelectedItem(undefined);
    setIsSendTestDialogOpen(false);
  };

  const isSaveButtonDisabled = (projectCustomerEmailType: ProjectCustomerEmailItem) => {
    if (!project.customer_email) {
      return true;
    }

    const email = project.customer_email![emailType];

    if (!email) return true;

    const emailBody = htmlToString(email.body);
    const projectCustomerTypeBody = htmlToString(projectCustomerEmailType.body);

    return (
      emailBody === projectCustomerTypeBody &&
      email.from === projectCustomerEmailType.from &&
      email.subject === projectCustomerEmailType.subject
    );
  };

  if (!emailTemplate) return null;

  const isAttachmentEnabled = (attachmentId: string) => {
    const withAttachments = [`stop_completed`, `stop_canceled`, `pickup_completed`, `pickup_canceled`];

    if (!withAttachments.includes(emailType) || !availablePod) {
      return false;
    }

    const found = (emailTemplate.pod_fields ?? []).find((pod) => pod === attachmentId);

    return found !== undefined;
  };

  const getNumOfAttachments = () =>
    [isAttachmentEnabled(`pictures`), isAttachmentEnabled(`signature`)].filter((item) => item === true).length;

  return (
    <>
      <NotificationsEmailContainer>
        <NotificationsEmailContainerHeader>
          <Grid item>
            <Text
              variant="body-large"
              intlId={`projects.communication.messages.emails.${project.getTranslationsEmailType(emailType)}`}
            />
          </Grid>

          <Grid item>
            <Grid container direction="row" alignItems="center" justifyContent="flex-end" wrap="nowrap">
              <RoutalSelect
                id="more-actions"
                style={{
                  justifyContent: `space-between`,
                }}
                containerStyle={{
                  width: `120px`,
                }}
                popoverProps={{
                  anchor: {
                    anchorOrigin: {
                      vertical: `bottom`,
                      horizontal: `right`,
                    },
                    transformOrigin: {
                      vertical: `top`,
                      horizontal: `right`,
                    },
                  },
                  options: [
                    ...(availablePod
                      ? [
                          {
                            key: `edit_report`,
                            width: 220,
                            content:
                              availablePod.length > 0 ? (
                                <Grid container direction="row" alignItems="center" wrap="nowrap">
                                  <StopReportIcon style={{ width: `12px` }} />
                                  <HSpacer small />
                                  <Text
                                    style={{ cursor: `pointer` }}
                                    intlId="projects.communication.messages.emails.proof_of_delivery"
                                  />
                                </Grid>
                              ) : (
                                <RoutalTooltip
                                  placement="top"
                                  overlayInnerStyle={{ color: RoutalPalette.neutral00 }}
                                  title={
                                    <Text
                                      tagName="span"
                                      intlId="projects.communication.messages.emails.proof_of_delivery.disabled"
                                    />
                                  }
                                >
                                  <Grid container direction="row" alignItems="center" wrap="nowrap">
                                    <StopReportIcon style={{ width: `12px` }} />
                                    <HSpacer small />
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      intlId="projects.communication.messages.emails.proof_of_delivery"
                                    />
                                  </Grid>
                                </RoutalTooltip>
                              ),
                            onClick: () => {
                              setSelectedItem(emailTemplate);
                              setIsPodDialogOpen(true);
                            },
                          },
                        ]
                      : []),
                    {
                      key: `send_test`,
                      width: 220,
                      content: (
                        <Grid container direction="row" alignItems="center">
                          <MessageIcon style={{ width: `12px` }} />
                          <HSpacer small />
                          <Text
                            style={{ cursor: `pointer` }}
                            intlId="projects.communication.messages.emails.send_test.button"
                          />
                        </Grid>
                      ),
                      onClick: () => {
                        setSelectedItem(emailTemplate);
                        setTestEmail(userEmail ?? ``);
                        setIsSendTestDialogOpen(true);
                      },
                    },
                  ],
                }}
              >
                <Text intlId="projects.communication.messages.emails.options_button" />
              </RoutalSelect>

              <HSpacer small />

              <Button
                id={`save-email-template`}
                styleType="primary"
                variant="default"
                disabled={isSaveButtonDisabled(emailTemplate)}
                onClick={() => {
                  updateEmailItem(emailTemplate);
                }}
                intlId="save"
              />

              <HSpacer small />

              <Button
                id={`${emailTemplate.enabled ? `disable` : `enable`}-email-template`}
                styleType="secondary"
                variant="default"
                style={{
                  backgroundColor: emailTemplate.enabled ? RoutalPalette.error.medium : RoutalPalette.success.medium,
                }}
                onClick={() => {
                  if (!emailTemplate.enabled) {
                    setSelectedItem(
                      ProjectCustomerEmailItem.create({
                        ...emailTemplate.getValue(),
                        enabled: true,
                      })
                    );

                    setIsEnableEmailDialogOpen(true);
                  } else {
                    updateEmailItem(
                      ProjectCustomerEmailItem.create({
                        ...emailTemplate.getValue(),
                        enabled: false,
                      })
                    );
                  }
                }}
                intlId={`projects.communication.messages.emails.${
                  emailTemplate.enabled ? `disable_button` : `enable_button`
                }`}
              />
            </Grid>
          </Grid>
        </NotificationsEmailContainerHeader>

        <div
          style={{
            padding: `20px`,
            overflowY: `auto`,
            height: `calc(100% - 111px)`,
          }}
        >
          <NotificationsEmailContainerBody>
            <Grid
              container
              direction="column"
              style={{
                borderBottom: `1px solid ${RoutalPalette.neutral10}`,
              }}
            >
              <Grid
                item
                container
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                wrap="nowrap"
                style={{
                  padding: `8px 0`,
                  backgroundColor: RoutalPalette.neutral00,
                  zIndex: 1,
                  borderTopLeftRadius: `4px`,
                  borderTopRightRadius: `4px`,
                  borderBottom: `1px solid ${RoutalPalette.neutral10}`,
                }}
              >
                <Grid item container direction="row" alignItems="center" style={{ padding: `0 22px` }} wrap="nowrap">
                  <Text tagName="span" variant="label" intlId="projects.communication.messages.emails.from" />

                  <HSpacer medium />

                  <Grid item style={{ flex: 1 }}>
                    <InputText
                      fieldValue={emailTemplate.from ?? ``}
                      placeholder={intl.formatMessage({ id: `projects.communication.messages.emails.from_ph` })}
                      onChange={(value: any) => {
                        emailTemplate.from = value !== `` ? value : undefined;
                        setEmailTemplate(ProjectCustomerEmailItem.create(emailTemplate.getValue()));
                      }}
                      inputContainerStyles={{
                        width: `100%`,
                        border: `none`,
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Text style={{ color: `#CDCFD0` }}>{`<no-reply@routal.com>`}</Text>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                container
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                wrap="nowrap"
                style={{ padding: `8px 0`, backgroundColor: RoutalPalette.neutral00, zIndex: 1 }}
              >
                <Grid item container direction="row" alignItems="center" style={{ padding: `0 22px` }}>
                  <InputText
                    fieldValue={emailTemplate.subject ?? ``}
                    placeholder={intl.formatMessage({ id: `projects.communication.messages.emails.subject_ph` })}
                    onChange={(value: any) => {
                      emailTemplate.subject = value !== `` ? value : null;
                      setEmailTemplate(ProjectCustomerEmailItem.create(emailTemplate.getValue()));
                    }}
                    inputContainerStyles={{
                      width: `100%`,
                      border: `none`,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="center"
              style={{
                height: `100%`,
                flexDirection: `column`,
                justifyContent: `start`,
                alignItems: `center`,
                position: `relative`,
                overflow: `hidden`,
              }}
            >
              <div
                style={{
                  position: `absolute`,
                  width: `100%`,
                  height: `100%`,
                  top: 0,
                  left: 0,
                  backgroundColor: RoutalPalette.neutral05,
                }}
              />

              <NotificationsEmailBody
                style={{
                  zIndex: 2,
                  marginBottom: `22px`,
                  border: `1px solid ${RoutalPalette.neutral10}`,
                  borderTop: `none`,
                  borderBottom: `none`,
                  overflowY: `auto`,
                }}
                onMouseLeave={() => {
                  setVisibleSubstitutions(false);
                }}
                onClick={() => {
                  setVisibleSubstitutions(false);
                }}
              >
                {project.customers.style.logo ? (
                  <Grid item container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
                    <Grid item container direction="column" alignItems="center" style={{ height: `120px` }}>
                      <VSpacer medium />

                      <Grid
                        item
                        style={{
                          marginBottom: `8px`,
                        }}
                      >
                        <img
                          src={project.customers.style.logo}
                          draggable="false"
                          alt="customers_logo"
                          style={{ width: `84px`, height: `84px` }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                <Grid container direction="column" style={{ padding: `0 20px`, minHeight: `60px` }}>
                  <VSpacer medium />

                  <Grid item container justifyContent="center">
                    <Text
                      tagName="span"
                      style={{
                        color: project.customers.style.secondary_color?.value ?? RoutalPalette.text,
                        fontSize: `34px`,
                        fontWeight: `bold`,
                        textAlign: `center`,
                        width: `100%`,
                        wordBreak: `break-word`,
                      }}
                    >
                      {emailTemplate.subject ??
                        intl.formatMessage({
                          id: `projects.communication.messages.emails.${project.getTranslationsEmailType(emailType)}`,
                        })}
                    </Text>
                  </Grid>
                </Grid>

                <VSpacer large />

                <Grid container direction="column" style={{ padding: `0 20px` }}>
                  <Grid container direction="column" wrap="nowrap">
                    <Grid
                      item
                      style={{
                        position: `relative`,
                        width: `calc(100% - 36px)`,
                        marginLeft: `16px`,
                      }}
                    >
                      <EditableContentEmail
                        value={emailTemplate.body ?? ``}
                        inputStyle={{
                          fontFamily: `Geist Variable`,
                          fontSize: `14px`,
                          outline: `unset`,
                          padding: `18.5px 14px`,
                          borderRadius: `2px`,
                          border: `1px solid #CDCFD0`,
                          minHeight: `80px`,
                          overflowWrap: `anywhere`,
                        }}
                        placeholder={intl.formatMessage({
                          id: `projects.communication.messages.emails.${project.getTranslationsEmailType(
                            emailType
                          )}.placeholder`,
                        })}
                        onValueChange={(value) => {
                          emailTemplate.body = value !== `` ? value : undefined;
                          setEmailTemplate(ProjectCustomerEmailItem.create(emailTemplate.getValue()));
                        }}
                        visibleSubstitutions={visibleSubstitutions}
                        setVisibleSubstitutions={setVisibleSubstitutions}
                        substitutions={[
                          ...customerEmailSubstitutions.map((substitution) => ({
                            id: substitution,
                            label: intl.formatMessage({
                              id: `projects.communication.messages.emails.substitutions.${substitution}`,
                            }),
                          })),
                          ...(project?.custom_fields?.client
                            ? project.custom_fields.client.map((customField: CustomFieldDefinition) => ({
                                id: `${CUSTOM_FIELD_PREFIX}${customField.custom_id}`,
                                label: customField.label,
                              }))
                            : []),
                        ]}
                      />
                    </Grid>

                    {isAttachmentEnabled(`comments`) ? (
                      <>
                        <VSpacer medium />
                        <Grid item container direction="column" wrap="nowrap">
                          <Grid item>
                            <Text
                              variant="body-large"
                              intlId="projects.communication.messages.sms.substitutions.comments"
                            />
                          </Grid>
                          <VSpacer small />
                          <Grid item container direction="row" style={{ gap: `8px` }}>
                            {[60, 50, 60, 40, 20, 40, 30, 60, 70, 80, 10, 40, 70, 20, 40, 30, 30].map(
                              (width, index) => (
                                <Skeleton
                                  key={index}
                                  variant="rectangular"
                                  animation={false}
                                  height={20}
                                  width={width}
                                />
                              )
                            )}
                          </Grid>
                        </Grid>
                      </>
                    ) : null}

                    <VSpacer large />

                    <Grid item container justifyContent="center">
                      <Button
                        styleType="primary"
                        variant="default"
                        onClick={() => {
                          window.open(`${getCustomersLink()}`, `_blank`);
                        }}
                        size="large"
                        style={{
                          backgroundColor: project.customers.style.primary_color?.value ?? RoutalPalette.primary40,
                        }}
                      >
                        <Text
                          style={{
                            color: RoutalPalette.oldWhite,
                          }}
                          intlId={
                            [`stop_completed`, `stop_canceled`, `pickup_completed`, `pickup_canceled`].includes(
                              emailType
                            )
                              ? `projects.communication.messages.emails.view_report_button`
                              : `projects.communication.messages.emails.follow_route_button`
                          }
                        />
                      </Button>
                    </Grid>

                    {project.isCustomersSurveyEnabled() &&
                    [`stop_completed`, `pickup_completed`].includes(emailType) ? (
                      <>
                        <VSpacer large />

                        <Grid container direction="column" justifyContent="center" alignItems="center">
                          <VSpacer large />

                          <Text
                            variant="h6"
                            intlId="projects.communication.messages.emails.survey_title"
                            style={{ textAlign: `center`, maxWidth: `220px` }}
                          />

                          <VSpacer large />

                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            style={{ maxWidth: `220px` }}
                          >
                            {Array(5)
                              .fill(0)
                              .map((_, index) => {
                                const isSelected = index + 1 <= 4;

                                return (
                                  <Grid
                                    key={`star-${index}`}
                                    item
                                    style={{
                                      width: `34px`,
                                      height: `34px`,
                                      marginLeft: index !== 0 ? `6px` : `0px`,
                                    }}
                                  >
                                    <RatingStar style={{ width: `34px`, height: `34px` }} isSelected={isSelected} />
                                  </Grid>
                                );
                              })}
                          </Grid>
                        </Grid>

                        <VSpacer large />
                      </>
                    ) : null}
                  </Grid>
                </Grid>
                <NotificationsEmailBodyFooter customersStyle={project.customers.style} />
              </NotificationsEmailBody>
            </Grid>

            {availablePod && getNumOfAttachments() > 0 ? (
              <Grid
                container
                direction="column"
                style={{
                  padding: `12px 22px`,
                  paddingBottom: `22px`,
                  zIndex: 2,
                  borderTop: `1px solid ${RoutalPalette.neutral10}`,
                }}
              >
                <Text
                  variant="body-large"
                  isBold
                  intlId={`projects.communication.messages.emails.attachments${
                    getNumOfAttachments() === 1 ? `_singular` : ``
                  }`}
                  intlValues={{
                    number: getNumOfAttachments(),
                  }}
                />
                <VSpacer small />
                <Grid item container direction="row">
                  {isAttachmentEnabled(`pictures`) ? (
                    <>
                      <AttachmentItem>
                        <CommunicationPictureIcon style={{ width: `55px`, height: `50px` }} />
                      </AttachmentItem>
                      <HSpacer small />
                    </>
                  ) : null}
                  {isAttachmentEnabled(`signature`) ? (
                    <AttachmentItem>
                      <CommunicationSignatureIcon style={{ width: `55px`, height: `50px`, marginLeft: `12px` }} />
                    </AttachmentItem>
                  ) : null}
                </Grid>
              </Grid>
            ) : null}
          </NotificationsEmailContainerBody>
        </div>
      </NotificationsEmailContainer>

      <RoutalDialog
        open={!!isEnableEmailDialogOpen}
        dialogProps={{
          fullWidth: true,
          maxWidth: `sm`,
        }}
        onCloseDialog={() => {
          setIsEnableEmailDialogOpen(false);
          setSelectedItem(undefined);
        }}
        titleContent={<Text variant="h6" intlId="projects.communication.messages.emails.enable.title" />}
        footerContent={
          <Grid container justifyContent="flex-end">
            <Button
              variant="flat"
              onClick={() => {
                setIsEnableEmailDialogOpen(false);
                setSelectedItem(undefined);
              }}
              intlId="cancel"
            />

            <HSpacer small />

            <Button
              id="enable-email-template"
              styleType="primary"
              onClick={async () => {
                await updateEmailItem(selectedItem);
                setSelectedItem(undefined);
                setIsEnableEmailDialogOpen(false);
              }}
              intlId="confirm"
            />
          </Grid>
        }
      >
        <Grid container direction="column">
          <Text intlId="projects.communication.messages.emails.enable.description" />
        </Grid>
      </RoutalDialog>

      <RoutalDialog
        open={!!isPodDialogOpen}
        dialogProps={{
          fullWidth: true,
          maxWidth: `sm`,
        }}
        onCloseDialog={() => {
          setIsPodDialogOpen(false);
          setSelectedItem(undefined);
        }}
        titleContent={<Text variant="h6" intlId="projects.communication.messages.emails.proof_of_delivery" />}
        showFooter={false}
      >
        <Grid container direction="column">
          <Text intlId="projects.communication.messages.emails.proof_of_delivery.description" />

          <VSpacer large />

          {(availablePod ?? []).map((field: IPodField) => (
            <Grid
              key={field.id}
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              wrap="nowrap"
            >
              <Grid item container direction="row" alignItems="center">
                {field.icon}
                <HSpacer small />
                <Text>{field.label}</Text>
              </Grid>
              <Grid item>
                <Switch
                  checked={(emailTemplate.pod_fields ?? []).indexOf(field.id) > -1}
                  value={(emailTemplate.pod_fields ?? []).indexOf(field.id) > -1}
                  onChange={(event: any) => {
                    const { checked } = event.target;

                    let podFields = emailTemplate.pod_fields ?? [];

                    if (!checked) {
                      podFields = podFields.filter((pod) => pod !== field.id);
                    } else {
                      podFields.push(field.id);
                    }

                    updateEmailItem(
                      ProjectCustomerEmailItem.create({
                        ...emailTemplate.getValue(),
                        pod_fields: podFields,
                      })
                    );
                  }}
                  color="primary"
                />
              </Grid>
            </Grid>
          ))}

          <VSpacer large />
        </Grid>
      </RoutalDialog>

      <RoutalDialog
        open={!!isSendTestDialogOpen}
        dialogProps={{
          fullWidth: true,
          maxWidth: `sm`,
        }}
        onCloseDialog={() => {
          setIsSendTestDialogOpen(false);
          setSelectedItem(undefined);
        }}
        titleContent={<Text variant="h6" intlId="projects.communication.messages.emails.send_test.title" />}
        footerContent={
          <Grid container justifyContent="flex-end">
            <Button
              variant="flat"
              onClick={() => {
                setIsSendTestDialogOpen(false);
                setSelectedItem(undefined);
              }}
              intlId="cancel"
            />

            <HSpacer small />

            <Button
              disabled={!testEmail || testEmail === `` || !validateEmail(testEmail)}
              onClick={async () => sendTestItem()}
              styleType="primary"
              intlId="send"
            />
          </Grid>
        }
      >
        <Grid container direction="column">
          <Text intlId="projects.communication.messages.emails.send_test.description" />

          <VSpacer large />

          <InputText
            id="email"
            type="email"
            placeholderIntlId="login.email"
            fieldValue={testEmail}
            required
            onChange={(value: any) => {
              setTestEmail(value);
            }}
            validators={[
              {
                errorMessage: (
                  <Text
                    tagName="span"
                    style={{ cursor: `default`, color: RoutalPalette.error.medium }}
                    intlId="projects.communication.messages.emails.send_test.invalid_email"
                  />
                ),
                isInvalid: (text?: string) => !text || !validateEmail(text),
              },
            ]}
          />
        </Grid>
      </RoutalDialog>
    </>
  );
};
